/**
 * @file FrameNumbers.tsx
 * @description Числа шага «Кадр под блок» (phase19.16, партия SB120, этап 1;
 * uiux-sb120-stage1 §2.4, §3): у фото фона — масштаб 1–4 × (`zoom` /
 * `zoomPhone`, телефон ↺ как на ПК); у элемента «Фото» при высоте «По фото» —
 * пропорции кадра 0,2–6 (`aspect` / `aspectPhone`; масштаба у элемента в
 * схеме нет). Нет ключа — «без масштаба» / «как у файла» / «как на ПК».
 */

import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import type { FrameDraft } from "../../../../lib/site-builder/frame-draft";
import type { Device } from "../../../../lib/site-builder/look-size";

/** Пропорции кадра — подсказки (ширина ÷ высота). */
const ASPECT_PRESETS = [
  { label: "1:1", value: 1 },
  { label: "4:3", value: 1.333 },
  { label: "3:4", value: 0.75 },
  { label: "16:9", value: 1.778 },
  { label: "3:2", value: 1.5 },
];

interface Props {
  numbers: "zoom" | "aspect" | "none";
  device: Device;
  draft: FrameDraft;
  onChange: (draft: FrameDraft) => void;
}

export function FrameNumbers({ numbers, device, draft, onChange }: Props) {
  if (numbers === "none") return null;
  const phone = device === "phone";
  const zoom = numbers === "zoom";
  const key: keyof FrameDraft = zoom ? (phone ? "zoomPhone" : "zoom") : phone ? "aspectPhone" : "aspect";
  const desktop = zoom ? draft.zoom : draft.aspect;
  const set = (value: number | undefined) => {
    const next = { ...draft };
    if (value === undefined) delete next[key];
    else next[key] = value;
    onChange(next);
  };
  return (
    <NumberSliderField
      label={zoom ? "Масштаб" : "Пропорции кадра"}
      value={draft[key] as number | undefined}
      inherit={phone ? { value: desktop ?? (zoom ? 1 : undefined), label: "как на ПК" } : zoom ? { value: 1, label: "без масштаба" } : { label: "как у файла" }}
      min={zoom ? 1 : 0.2}
      max={zoom ? 4 : 6}
      step={zoom ? 0.05 : 0.01}
      unit="×"
      display={{ factor: 1, decimals: 2 }}
      presets={zoom ? undefined : ASPECT_PRESETS}
      hint={zoom ? undefined : "Ширина ÷ высота: 1 — квадрат, больше 1 — шире, меньше 1 — выше."}
      onChange={set}
    />
  );
}
